import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const emptyForm = {
    name: "",
    monthlyPrice: "",
    additionalInfo: "",
    paymentDayMode: "FLEXIBLE",
    fixedPaymentDays: [],
    questions: [],
};

const newQuestion = () => ({
    question: "",
    type: "text",
    options: [],
});

const ContractRequestPlanModal = ({ open, plan, onClose, onSave, onDelete }) => {
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(plan ? {
            ...emptyForm,
            ...plan,
            monthlyPrice: plan.monthlyPrice ?? "",
            fixedPaymentDays: Array.isArray(plan.fixedPaymentDays) ? plan.fixedPaymentDays : [],
            questions: Array.isArray(plan.questions)
                ? [...plan.questions]
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((question) => ({
                        ...question,
                        options: Array.isArray(question.options) ? question.options : [],
                    }))
                : [],
        } : emptyForm);
        setError("");
    }, [open, plan]);

    const setField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const updateQuestion = (index, changes) => {
        setForm((current) => ({
            ...current,
            questions: current.questions.map((question, questionIndex) =>
                questionIndex === index ? { ...question, ...changes } : question
            ),
        }));
    };

    const removeQuestion = (index) => {
        setForm((current) => ({
            ...current,
            questions: current.questions.filter((_, questionIndex) => questionIndex !== index),
        }));
    };

    const addOption = (questionIndex) => {
        const options = [...form.questions[questionIndex].options, { label: "", description: "" }];
        updateQuestion(questionIndex, { options });
    };

    const updateOption = (questionIndex, optionIndex, changes) => {
        const options = form.questions[questionIndex].options.map((option, index) =>
            index === optionIndex ? { ...option, ...changes } : option
        );
        updateQuestion(questionIndex, { options });
    };

    const removeOption = (questionIndex, optionIndex) => {
        const options = form.questions[questionIndex].options.filter((_, index) => index !== optionIndex);
        updateQuestion(questionIndex, { options });
    };

    const validate = () => {
        if (!form.name.trim()) return "Name is required.";
        if (form.monthlyPrice === "" || Number(form.monthlyPrice) < 0) return "Enter a valid monthly price.";
        if (form.paymentDayMode === "FIXED" && form.fixedPaymentDays.length === 0) {
            return "Select at least one fixed payment day.";
        }
        for (const question of form.questions) {
            if (!question.question.trim()) return "Every question must have text.";
            if (question.type === "radio") {
                if (question.options.length < 2) return "Radio questions need at least two options.";
                if (question.options.some((option) => !option.label.trim())) {
                    return "Every radio option must have a label.";
                }
            }
        }
        return "";
    };

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        const payload = {
            name: form.name.trim(),
            monthlyPrice: Number(form.monthlyPrice),
            additionalInfo: form.additionalInfo.trim(),
            paymentDayMode: form.paymentDayMode,
            fixedPaymentDays: form.paymentDayMode === "FIXED"
                ? [...new Set(form.fixedPaymentDays.map(Number))].sort((a, b) => a - b)
                : [],
            questions: form.questions.map((question, index) => ({
                ...(question.id ? { id: question.id } : {}),
                question: question.question.trim(),
                type: question.type,
                options: question.type === "radio"
                    ? question.options.map((option) => ({
                        label: option.label.trim(),
                        description: option.description.trim(),
                    }))
                    : [],
                order: index,
            })),
        };

        setSaving(true);
        setError("");
        try {
            await onSave(payload);
        } catch (saveError) {
            setError(saveError.message || "Could not save contract request.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${form.name}"?`)) return;
        setSaving(true);
        setError("");
        try {
            await onDelete(plan.id);
        } catch (deleteError) {
            setError(deleteError.message || "Could not delete contract request.");
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>{plan ? "Edit Contract Request" : "Add Contract Request"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(event) => setField("name", event.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Monthly price"
                        type="number"
                        value={form.monthlyPrice}
                        onChange={(event) => setField("monthlyPrice", event.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Additional information"
                        value={form.additionalInfo}
                        onChange={(event) => setField("additionalInfo", event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Payment day mode</InputLabel>
                        <Select
                            value={form.paymentDayMode}
                            label="Payment day mode"
                            onChange={(event) => setField("paymentDayMode", event.target.value)}
                        >
                            <MenuItem value="FLEXIBLE">Customer chooses</MenuItem>
                            <MenuItem value="FIXED">Fixed options</MenuItem>
                        </Select>
                    </FormControl>
                    {form.paymentDayMode === "FIXED" && (
                        <FormControl fullWidth>
                            <InputLabel>Fixed payment days</InputLabel>
                            <Select
                                multiple
                                value={form.fixedPaymentDays}
                                label="Fixed payment days"
                                onChange={(event) => setField("fixedPaymentDays", event.target.value)}
                                renderValue={(days) => [...days].sort((a, b) => a - b).join(", ")}
                            >
                                {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Alert severity="info">
                        Customers are always asked for their start date and monthly payment due date.
                    </Alert>

                    <Divider />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6">Required questions</Typography>
                        <Button
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => setField("questions", [...form.questions, newQuestion()])}
                        >
                            Add question
                        </Button>
                    </Box>

                    {form.questions.map((question, questionIndex) => (
                        <Box key={question.id || questionIndex} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
                            <Stack spacing={2}>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                    <TextField
                                        label={`Question ${questionIndex + 1}`}
                                        value={question.question}
                                        onChange={(event) => updateQuestion(questionIndex, { question: event.target.value })}
                                        required
                                        fullWidth
                                    />
                                    <FormControl sx={{ minWidth: 140 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={question.type}
                                            label="Type"
                                            onChange={(event) => updateQuestion(questionIndex, {
                                                type: event.target.value,
                                                options: event.target.value === "radio" ? question.options : [],
                                            })}
                                        >
                                            <MenuItem value="text">Text</MenuItem>
                                            <MenuItem value="radio">Radio</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <IconButton color="error" onClick={() => removeQuestion(questionIndex)}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>

                                {question.type === "radio" && (
                                    <Stack spacing={1.5}>
                                        {question.options.map((option, optionIndex) => (
                                            <Box key={optionIndex} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                                <TextField
                                                    label="Option label"
                                                    value={option.label}
                                                    onChange={(event) => updateOption(questionIndex, optionIndex, { label: event.target.value })}
                                                    required
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Description"
                                                    value={option.description}
                                                    onChange={(event) => updateOption(questionIndex, optionIndex, { description: event.target.value })}
                                                    fullWidth
                                                />
                                                <IconButton color="error" onClick={() => removeOption(questionIndex, optionIndex)}>
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        <Button onClick={() => addOption(questionIndex)} startIcon={<AddCircleOutlineIcon />}>
                                            Add option
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {plan && (
                    <Button color="error" onClick={handleDelete} disabled={saving} sx={{ mr: "auto" }}>
                        Delete
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContractRequestPlanModal;
